
import { useState, useRef, useEffect } from 'react';

export const useMediaDeviceSetup = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  
  const setupMediaDevices = async (): Promise<boolean> => {
    try {
      // Only request mic access if we don't already have a stream
      if (!streamRef.current) {
        console.log("Requesting microphone access for recording...");
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          },
          video: false // Explicitly set video to false to prevent camera activation
        });
        streamRef.current = stream;
        
        // Set up audio context for level monitoring
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = audioContext;
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyserRef.current = analyser;
        
        const micSource = audioContext.createMediaStreamSource(stream);
        micSource.connect(analyser);
        micSourceRef.current = micSource;
      }
      
      if (!streamRef.current) {
        throw new Error("Failed to get audio stream");
      }
      
      setErrorMessage(null);
      return true;
    } catch (err) {
      console.error("Error setting up media devices:", err);
      setErrorMessage(`Could not access microphone: ${err instanceof Error ? err.message : String(err)}`);
      return false;
    }
  };
  
  // Cleanup function for media devices
  const cleanupMediaDevices = () => {
    // Release microphone
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    if (analyserRef.current) {
      analyserRef.current = null;
    }
    
    if (micSourceRef.current) {
      micSourceRef.current = null;
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupMediaDevices();
    };
  }, []);
  
  return {
    errorMessage,
    streamRef,
    analyserRef,
    audioContextRef,
    setupMediaDevices,
    cleanupMediaDevices,
    setErrorMessage
  };
};
